import { Box, Typography, InputAdornment } from '@mui/material';
import { useMemo } from 'react';
import { AutocompleteOption } from '@/common/presentation/hooks/useAutocomplete';
import FormikAutocomplete from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { ERPJobFormValues } from '../../../../ERPJobValidationSchema';
import { useFormikContext } from 'formik';
import { useSpecialFlagAutocomplete } from '@/jobs/presentation/hooks/useSpecialFlagAutocomplete';
import useAvailableSpecialFlags from '@/raptorjob/application/adapters/useAvailableSpecialFlags';
import tid from '@/common/infrastructure/config/tid';
import { getFlagImage } from '@/jobs/presentation/utils/getFlagImage';
import type { NamedEntity } from '@/raptorjob/application/utils/jobDataExtractor';

interface ERPFlagsSectionProps {
  flag1?: NamedEntity | null;
  flag2?: NamedEntity | null;
  flag3?: NamedEntity | null;
}

export function ERPFlagsSection({ flag1, flag2, flag3 }: ERPFlagsSectionProps = {}) {
  const { values } = useFormikContext<ERPJobFormValues>();
  const { availableSpecialFlags, loading: specialFlagLoading } = useAvailableSpecialFlags();

  // Special Flag 1
  const {
    specialFlagOptions: specialFlag1Options,
    specialFlagFilterOptions: specialFlag1FilterOptions,
    handleSpecialFlagInputChange: handleSpecialFlag1InputChange,
    initialSpecialFlagOption: initialSpecialFlag1Option,
    loading: specialFlag1AutocompleteLoading
  } = useSpecialFlagAutocomplete({
    availableSpecialFlags,
    loading: specialFlagLoading,
    rawSpecialFlag: flag1 ? { id: flag1.id, name: flag1.name } : undefined,
    selectedSpecialFlagId: values.flag1Id ?? null
  });

  // Special Flag 2
  const {
    specialFlagOptions: specialFlag2Options,
    specialFlagFilterOptions: specialFlag2FilterOptions,
    handleSpecialFlagInputChange: handleSpecialFlag2InputChange,
    initialSpecialFlagOption: initialSpecialFlag2Option,
    loading: specialFlag2AutocompleteLoading
  } = useSpecialFlagAutocomplete({
    availableSpecialFlags,
    loading: specialFlagLoading,
    rawSpecialFlag: flag2 ? { id: flag2.id, name: flag2.name } : undefined,
    selectedSpecialFlagId: values.flag2Id ?? null
  });

  // Special Flag 3
  const {
    specialFlagOptions: specialFlag3Options,
    specialFlagFilterOptions: specialFlag3FilterOptions,
    handleSpecialFlagInputChange: handleSpecialFlag3InputChange,
    initialSpecialFlagOption: initialSpecialFlag3Option,
    loading: specialFlag3AutocompleteLoading
  } = useSpecialFlagAutocomplete({
    availableSpecialFlags,
    loading: specialFlagLoading,
    rawSpecialFlag: flag3 ? { id: flag3.id, name: flag3.name } : undefined,
    selectedSpecialFlagId: values.flag3Id ?? null
  });

  // Memoize flag options with fallback from jobData
  const flag1OptionsWithFallback = useMemo(() => {
    if (flag1 && flag1.id && flag1.name) {
      const exists = specialFlag1Options.some((opt) => Number(opt.value) === flag1.id);
      if (!exists) {
        return [
          ...specialFlag1Options,
          {
            value: flag1.id,
            label: flag1.name
          }
        ];
      }
    }
    return specialFlag1Options;
  }, [specialFlag1Options, flag1]);

  const flag2OptionsWithFallback = useMemo(() => {
    if (flag2 && flag2.id && flag2.name) {
      const exists = specialFlag2Options.some((opt) => Number(opt.value) === flag2.id);
      if (!exists) {
        return [
          ...specialFlag2Options,
          {
            value: flag2.id,
            label: flag2.name
          }
        ];
      }
    }
    return specialFlag2Options;
  }, [specialFlag2Options, flag2]);

  const flag3OptionsWithFallback = useMemo(() => {
    if (flag3 && flag3.id && flag3.name) {
      const exists = specialFlag3Options.some((opt) => Number(opt.value) === flag3.id);
      if (!exists) {
        return [
          ...specialFlag3Options,
          {
            value: flag3.id,
            label: flag3.name
          }
        ];
      }
    }
    return specialFlag3Options;
  }, [specialFlag3Options, flag3]);

  return (
    <>
      <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: '12px', fontWeight: 600 }}>
        Flags
      </Typography>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-around', gap: { xs: '0px', sm: '12px' }, flexDirection: { xs: 'column', sm: 'row' } }}
      >
        <FormikAutocomplete<AutocompleteOption, ERPJobFormValues>
          id="flag1Id"
          label="Flag 1"
          options={flag1OptionsWithFallback}
          optionsloading={specialFlag1AutocompleteLoading}
          initialOption={initialSpecialFlag1Option}
          filterOptions={specialFlag1FilterOptions}
          onInputChange={handleSpecialFlag1InputChange}
          getOptionLabel={(option) => option?.label ?? 'Unknown Flag'}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          startAdornment={
            values.flag1Id ? (
              <InputAdornment position="start" sx={{ mr: '8px', '& img': { marginRight: 0 } }}>
                {getFlagImage(values.flag1Id as number, false)}
              </InputAdornment>
            ) : null
          }
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getFlagImage(option.value as number)}
                <span>{option.label}</span>
              </Box>
            </li>
          )}
          sx={{ flex: 1 }}
          data-testid="raptor-jobs-create-job-flag-1-autocomplete"
          slotProps={{
            textField: {
              inputProps: {
                'data-testid': 'jobs:create:flag-1-autocomplete'
              }
            }
          }}
          {...tid('jobs:create:flag-1-autocomplete')}
        />
        <FormikAutocomplete<AutocompleteOption, ERPJobFormValues>
          id="flag2Id"
          label="Flag 2"
          options={flag2OptionsWithFallback}
          optionsloading={specialFlag2AutocompleteLoading}
          initialOption={initialSpecialFlag2Option}
          filterOptions={specialFlag2FilterOptions}
          onInputChange={handleSpecialFlag2InputChange}
          getOptionLabel={(option) => option?.label ?? 'Unknown Flag'}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          startAdornment={
            values.flag2Id ? (
              <InputAdornment position="start" sx={{ mr: '8px', '& img': { marginRight: 0 } }}>
                {getFlagImage(values.flag2Id as number, false)}
              </InputAdornment>
            ) : null
          }
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getFlagImage(option.value as number)}
                <span>{option.label}</span>
              </Box>
            </li>
          )}
          sx={{ flex: 1 }}
          data-testid="raptor-jobs-create-job-flag-2-autocomplete"
          slotProps={{
            textField: {
              inputProps: {
                'data-testid': 'jobs:create:flag-2-autocomplete'
              }
            }
          }}
          {...tid('jobs:create:flag-2-autocomplete')}
        />
        <FormikAutocomplete<AutocompleteOption, ERPJobFormValues>
          id="flag3Id"
          label="Flag 3"
          options={flag3OptionsWithFallback}
          optionsloading={specialFlag3AutocompleteLoading}
          initialOption={initialSpecialFlag3Option}
          filterOptions={specialFlag3FilterOptions}
          onInputChange={handleSpecialFlag3InputChange}
          getOptionLabel={(option) => option?.label ?? 'Unknown Flag'}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          startAdornment={
            values.flag3Id ? (
              <InputAdornment position="start" sx={{ mr: '8px', '& img': { marginRight: 0 } }}>
                {getFlagImage(values.flag3Id as number, false)}
              </InputAdornment>
            ) : null
          }
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getFlagImage(option.value as number)}
                <span>{option.label}</span>
              </Box>
            </li>
          )}
          sx={{ flex: 1 }}
          data-testid="raptor-jobs-create-job-flag-3-autocomplete"
          slotProps={{
            textField: {
              inputProps: {
                'data-testid': 'jobs:create:flag-3-autocomplete'
              }
            }
          }}
          {...tid('jobs:create:flag-3-autocomplete')}
        />
      </Box>
    </>
  );
}
